import * as React from 'react';
import { attachHardwareAutocomplete } from '../hardware-autocomplete';

type BenchmarkResultEditForm = {
    result: string;
    cpuName: string;
    cpuFrequency: string;
    cpuBusFrequency: string;
    cpuVoltage: string;
    cpuTemperature: string;
    cpuIsEngineeringSample: boolean;
    cpuUnlockedCores: string;
    cpuCount: string;
    memorySize: string;
    memoryModules: string;
    memoryFrequency: string;
    memoryTimings: string;
    memoryType: string;
    gpuName: string;
    gpuCount: string;
    motherboardName: string;
    motherboardVendor: string;
    motherboardBios: string;
    osName: string;
    osVersion: string;
    validationUrl: string;
    benchmarkTime: string;
    removeScreenshot: boolean;
};

type EditBenchmarkResultPage = {
    heading: string;
    backUrl: string;
    viewUrl?: string | null;
    postUrl: string;
    submitLabel: string;
    cpuLookupUrl: string;
    gpuLookupUrl: string;
    antiForgeryToken: string;
    hasScreenshot: boolean;
    errorMessage?: string | null;
    form: BenchmarkResultEditForm;
};

type HardwareAutocompleteProps = {
    label: string;
    field: 'cpuName' | 'gpuName';
    name: 'CpuName' | 'GpuName';
    value: string;
    required?: boolean;
    lookupUrl: string;
    onChange: (field: 'cpuName' | 'gpuName', value: string) => void;
};

type FormFieldProps = {
    label: string;
    field: keyof BenchmarkResultEditForm;
    name: string;
    value: string;
    type?: 'text' | 'number' | 'url' | 'datetime-local';
    step?: string;
    required?: boolean;
    onChange: (field: keyof BenchmarkResultEditForm, value: string) => void;
};

function FormField({ label, field, name, value, type = 'text', step, required, onChange }: FormFieldProps) {
    return (
        <div className="form-field">
            <label>
                {label}
                <span className="field-control">
                    <input
                        name={name}
                        type={type}
                        value={value}
                        step={step}
                        required={required}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(field, event.target.value)}
                    />
                </span>
            </label>
        </div>
    );
}

function HardwareAutocomplete({ label, field, name, value, required, lookupUrl, onChange }: HardwareAutocompleteProps) {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        if (inputRef.current === null) {
            return undefined;
        }

        return attachHardwareAutocomplete(inputRef.current, {
            lookupUrl,
            onSelect: (item) => onChange(field, item.label)
        });
    }, [field, lookupUrl, onChange]);

    return (
        <div className="form-field">
            <label>
                {label}
                <span className="field-control">
                    <input
                        ref={inputRef}
                        name={name}
                        type="text"
                        value={value}
                        required={required}
                        autoComplete="off"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(field, event.target.value)}
                    />
                </span>
            </label>
        </div>
    );
}

export function BenchmarkResultEdit({ page }: { page: EditBenchmarkResultPage }) {
    const [form, setForm] = React.useState<BenchmarkResultEditForm>(page.form);
    const [selectedScreenshot, setSelectedScreenshot] = React.useState<File | null>(null);

    const screenshotLabel = React.useMemo((): string => {
        if (selectedScreenshot !== null) {
            return selectedScreenshot.name;
        }

        return page.hasScreenshot ? 'Existing screenshot will be kept.' : 'No screenshot uploaded.';
    }, [page.hasScreenshot, selectedScreenshot]);

    const updateField = React.useCallback((field: keyof BenchmarkResultEditForm, value: string): void => {
        setForm((current) => ({
            ...current,
            [field]: value
        }));
    }, []);

    return (
        <form action={page.postUrl} method="post" encType="multipart/form-data">
            <input type="hidden" name="__RequestVerificationToken" value={page.antiForgeryToken} />

            {page.errorMessage ? <div className="alert">{page.errorMessage}</div> : null}

            <h2>Result</h2>
            <FormField label="Score" field="result" name="Result" value={form.result} type="number" step="any" required onChange={updateField} />
            <FormField label="Benchmark time" field="benchmarkTime" name="BenchmarkTime" value={form.benchmarkTime} type="datetime-local" onChange={updateField} />
            <FormField label="Validation URL" field="validationUrl" name="ValidationUrl" value={form.validationUrl} type="url" onChange={updateField} />

            <h2>CPU</h2>
            <HardwareAutocomplete label="CPU" field="cpuName" name="CpuName" value={form.cpuName} required lookupUrl={page.cpuLookupUrl} onChange={updateField} />
            <FormField label="CPU frequency (MHz)" field="cpuFrequency" name="CpuFrequency" value={form.cpuFrequency} type="number" step="any" required onChange={updateField} />
            <FormField label="CPU bus frequency (MHz)" field="cpuBusFrequency" name="CpuBusFrequency" value={form.cpuBusFrequency} type="number" step="any" onChange={updateField} />
            <FormField label="CPU voltage (V)" field="cpuVoltage" name="CpuVoltage" value={form.cpuVoltage} type="number" step="any" onChange={updateField} />
            <FormField label="CPU temperature (°C)" field="cpuTemperature" name="CpuTemperature" value={form.cpuTemperature} type="number" step="any" onChange={updateField} />
            <FormField label="Unlocked cores" field="cpuUnlockedCores" name="CpuUnlockedCores" value={form.cpuUnlockedCores} type="number" step="1" onChange={updateField} />
            <FormField label="CPU count" field="cpuCount" name="CpuCount" value={form.cpuCount} type="number" step="1" required onChange={updateField} />
            <div className="form-field">
                <label>
                    <span className="field-control field-control--checkbox">
                        <input
                            name="CpuIsEngineeringSample"
                            type="checkbox"
                            value="true"
                            checked={form.cpuIsEngineeringSample}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, cpuIsEngineeringSample: event.target.checked }))}
                        />
                        Engineering sample
                    </span>
                </label>
                <input name="CpuIsEngineeringSample" type="hidden" value="false" />
            </div>

            <h2>GPU</h2>
            <HardwareAutocomplete label="GPU" field="gpuName" name="GpuName" value={form.gpuName} lookupUrl={page.gpuLookupUrl} onChange={updateField} />
            <FormField label="GPU count" field="gpuCount" name="GpuCount" value={form.gpuCount} type="number" step="1" required onChange={updateField} />

            <h2>RAM</h2>
            <FormField label="Memory size (MB)" field="memorySize" name="MemorySize" value={form.memorySize} type="number" step="1" required onChange={updateField} />
            <FormField label="Memory modules" field="memoryModules" name="MemoryModules" value={form.memoryModules} type="number" step="1" required onChange={updateField} />
            <FormField label="Memory frequency (MHz)" field="memoryFrequency" name="MemoryFrequency" value={form.memoryFrequency} type="number" step="any" required onChange={updateField} />
            <FormField label="Memory timings" field="memoryTimings" name="MemoryTimings" value={form.memoryTimings} onChange={updateField} />
            <FormField label="Memory type" field="memoryType" name="MemoryType" value={form.memoryType} required onChange={updateField} />

            <h2>System</h2>
            <FormField label="Motherboard vendor" field="motherboardVendor" name="MotherboardVendor" value={form.motherboardVendor} onChange={updateField} />
            <FormField label="Motherboard name" field="motherboardName" name="MotherboardName" value={form.motherboardName} onChange={updateField} />
            <FormField label="BIOS version" field="motherboardBios" name="MotherboardBios" value={form.motherboardBios} onChange={updateField} />

            <h2>Software</h2>
            <FormField label="Operating system" field="osName" name="OsName" value={form.osName} required onChange={updateField} />
            <FormField label="Operating system version" field="osVersion" name="OsVersion" value={form.osVersion} onChange={updateField} />

            <h2>Screenshot</h2>
            <div className="form-field">
                <label>
                    Screenshot
                    <span className="field-control">
                        <input
                            name="ScreenshotFile"
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedScreenshot(event.target.files?.item(0) ?? null)}
                        />
                    </span>
                </label>
                <div className="field-help">{screenshotLabel}</div>
            </div>
            <div className="form-field">
                <label>
                    <span className="field-control field-control--checkbox">
                        <input
                            name="RemoveScreenshot"
                            type="checkbox"
                            value="true"
                            checked={form.removeScreenshot}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, removeScreenshot: event.target.checked }))}
                        />
                        Remove current screenshot
                    </span>
                </label>
                <input name="RemoveScreenshot" type="hidden" value="false" />
            </div>

            <button type="submit">{page.submitLabel}</button>
        </form>
    );
}
